import React from 'react';
import { NotepadText, House, CalendarClock } from 'lucide-react';
import './VisitCard.css';

const STATUS_CLASS_MAP = {
  new: 'status-new',
  'new day': 'status-new-day',
  hold: 'status-hold',
  complete: 'status-complete',
};

const getStatusClass = (card_status) => {
  if (!card_status) return '';
  return STATUS_CLASS_MAP[card_status.toLowerCase()] || 'status-default';
};

const VisitCard = ({ visit, onClick }) => {
  const calculateActualPercentLoss = (visit) => {
    const totalLoss = Number(visit.total_loss || 0);
    const voidTotal = Number(visit.void_total || 0);
    const invoiceTotal = Number(visit.invoice_total || 0);
  
    const actual_loss = totalLoss - voidTotal;
    const actual_total = invoiceTotal - voidTotal;
  
    if (actual_total <= 0) return '0%';
  
    const pct = (actual_loss / actual_total) * 100;
  
    if (pct >= 1000) {
      return `${pct.toFixed(0)}%`;
    }
  
    return `${pct.toFixed(1)}%`;
  };
  
  const formattedPercentLoss = calculateActualPercentLoss(visit);

  const formatLocalDatetime = (utcString) => {
    const date = new Date(utcString);
  
    const month = date.getMonth() + 1; 
    const day = date.getDate();
    const year = date.getFullYear().toString(); 
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
  
    hours = hours % 12;
    hours = hours === 0 ? 12 : hours; 
  
    return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
  };

  const formatCurrency = (value) => {
    const rounded = Number(value || 0).toFixed(0);
    const num = parseInt(rounded, 10);
  
    if (isNaN(num)) return '$0';
  
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
  
    return `$${num.toLocaleString()}`;
  };

  const truncate = (text, maxLength) =>
    text?.length > maxLength ? text.slice(0, maxLength - 1) + '…' : text;

  const maxVisibleChars = 30; 
  let runningLength = 0;
  const visibleLabels = [];
  const hiddenLabels = [];

  (visit.label || []).forEach((label) => {
    if (!label || typeof label.label_name !== 'string') {
      return; 
    }
  
    if (
      visibleLabels.length < 3 &&
      runningLength + label.label_name.length <= maxVisibleChars
    ) {
      visibleLabels.push(label);
      runningLength += label.label_name.length;
    } else {
      hiddenLabels.push(label);
    }
  });
  
  return (
    <div className="card" onClick={onClick}>
      <div className="card-content white-text">
        <div className="card__date">
          <span className="card__date__day">{formattedPercentLoss}</span>
          <span className="card__date__month">Loss</span>
        </div>

        <div className="card__connector-line" />

        <div className="card__meta">{visit.hospital_shortname}</div>

        <div className="card-title-row">
          {visit.ready_to_go && <House size={20} className="menu-icon-home" />}
          {visit.open_30_plus && <CalendarClock size={20} className="menu-icon-home" />}
          {visit.has_medical_notes && <NotepadText size={20} className="menu-icon" />}
          <span className="card-title-text">
            {visit.patient_name} ({visit.patient_pims_id})
          </span>
        </div>

        <p className="card-subtitle grey-text text-darken-2">
          <b>{visit.status}</b> - {truncate(visit.summary, 85)}
        </p>

        <div className="card-labels">
          
          <span className={`label-pill status ${getStatusClass(visit.card_status)}`}>
            {visit.card_status}
          </span>

          {visibleLabels.map((label, index) => (
            <span
              key={index}
              className="label-pill"
              style={{
                backgroundColor: label.color,
                color: '#ffffff',
                border: `1px solid ${label.color}`,
              }}
            >
              {label.label_name}
            </span>
          ))}

          {hiddenLabels.length > 0 && (
            <span
              className="label-pill more-labels"
              title={hiddenLabels.map((l) => l.label_name).join(', ')}
            >
              +{hiddenLabels.length}
            </span>
          )}
        </div>
      </div>

      <div className="card-footer">
        <div className="footer-left">
          <div className="check-in-header"><b>Check In:</b></div>
          {formatLocalDatetime(visit.check_in_at)}
        </div>

        <div className="footer-right">
          <div className="stat-block">
            <div className="stat-label">PVL</div>
            <div className="card-stat-value">{formatCurrency(visit.invoice_total - visit.total_loss)}</div>
          </div>
          <div
            className="stat-block"
            style={{
              backgroundColor: (visit.charge_capture_total + visit.failed_discounts) > 0 ? '#E7E7E7' : '',
            }}
          >
            <div className="stat-label"            
              style={{
                fontWeight: (visit.charge_capture_total + visit.failed_discounts) > 0 ? 600 : '',
                color: (visit.charge_capture_total + visit.failed_discounts) > 0 ? '#000000' : '',
              }}>Capture/Disc</div>
            <div className="card-stat-value">
              {formatCurrency(visit.charge_capture_total + visit.failed_discounts)}
            </div>
          </div>

          <div
            className="stat-block"
            style={{
              backgroundColor: (visit.missed_charge_amount) > 0 ? '#E7E7E7' : '',
            }}
          >
            <div className="stat-label"            
              style={{
                fontWeight: (visit.missed_charge_amount) > 0 ? 600 : '',
                color: (visit.missed_charge_amount) > 0 ? '#000000' : '',
              }}>Missed</div>
            <div className="card-stat-value">
              {formatCurrency(visit.missed_charge_amount)}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default VisitCard;
